import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Container,
    FormControlLabel,
    FormLabel,
    Paper,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { CreateTenantRequest } from '../../../application/types/createTenantRequest.ts';
import { AllowedTransactionRegistrations } from '../../../application/types/allowedTransactionRegistrations.ts';
import { useGlobalStore } from '../../../application/store/store.tsx';
import TenantService from './services/tenantService.ts';
import CircularProgress from '@mui/material/CircularProgress';
import { Save } from '@mui/icons-material';
import { NavigationRoutes } from '../../../application/router/routes.ts';

const CreateTenant = () => {
    const [tenant, setTenant] = useState<CreateTenantRequest>({
        id: '',
        name: '',
        adminEmail: '',
        issuer: '',
        connectionString: '',
        tenantConfig: {
            formRegistrationMethod: 'manual',
            allowedTransactionRegistrations:
                AllowedTransactionRegistrations.defaultRegistration,
        },
    });
    const { profile, setMessage } = useGlobalStore((state) => state);
    const tenantService = new TenantService();
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (
            name === 'formRegistrationMethod' ||
            name === 'allowedTransactionRegistrations'
        ) {
            setTenant({
                ...tenant,
                tenantConfig: {
                    ...tenant.tenantConfig,
                    [name]: value,
                },
            });
        } else {
            setTenant({ ...tenant, [name]: value });
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setTenant({
            ...tenant,
            issuer: profile.userName,
        });

        try {
            setIsLoading(true);
            const res = await tenantService.createTenant(tenant);
            if (res) {
                setMessage('Organisationen skapades', 'success', 6000);
                navigate(NavigationRoutes.afterCreateTenant + `/${res.id}`);
            }
        } catch (error) {
            setMessage('Kunde inte skapa organisationen', 'error', 6000);
            console.error('Error creating tenant:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Container maxWidth="md">
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('..')}
                color="primary"
            >
                Tillbaka
            </Button>
            <Paper elevation={3} className="p-8 border-r-6" component="form">
                <Typography variant="h4" align="center" gutterBottom>
                    Skapa ny organisation
                </Typography>
                <TextField
                    label="Namn"
                    name="name"
                    value={tenant.name}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="OrganisationsId"
                    name="id"
                    value={tenant.id}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Admin Email"
                    name="adminEmail"
                    value={tenant.adminEmail}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Anslutningssträng"
                    name="connectionString"
                    value={tenant.connectionString}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <Alert sx={{ p: 2, mb: 2 }} severity="info">
                    <Typography fontWeight="bold" gutterBottom>
                        Anslutningssträng behövs endast om organisationen har
                        sin egen databas.
                    </Typography>
                    <Typography fontWeight="body1" gutterBottom>
                        Exempel:
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                        Host=pillpaldevdb.postgres.database.azure.com;Port=5432;
                        Database=demo;Username=myUsername;Password=myPassword;
                    </Typography>
                </Alert>
                <FormLabel component="legend" className="mt-4">
                    Förvald inmatningsmetod
                </FormLabel>
                <RadioGroup
                    name="formRegistrationMethod"
                    value={tenant.tenantConfig.formRegistrationMethod}
                    onChange={handleChange}
                    row
                >
                    <FormControlLabel
                        value="manual"
                        control={<Radio />}
                        label="Manuell"
                    />
                    <FormControlLabel
                        value="barcode"
                        control={<Radio />}
                        label="Streckkodsläsare"
                    />
                </RadioGroup>
                <FormLabel component="legend" className="mt-4">
                    Tillåtna transaktionsregistreringar
                </FormLabel>
                <RadioGroup
                    className="mb-4"
                    name="allowedTransactionRegistrations"
                    value={tenant.tenantConfig.allowedTransactionRegistrations}
                    onChange={handleChange}
                    row
                >
                    <FormControlLabel
                        value={
                            AllowedTransactionRegistrations.defaultRegistration
                        }
                        control={<Radio />}
                        label="Grund"
                    />
                    <FormControlLabel
                        value={AllowedTransactionRegistrations.ambulance}
                        control={<Radio />}
                        label="Ambulans"
                    />
                    <FormControlLabel
                        value={AllowedTransactionRegistrations.vc}
                        control={<Radio />}
                        label="VC"
                    />
                </RadioGroup>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        mt: 2,
                    }}
                >
                    <Button
                        onClick={handleSubmit}
                        startIcon={
                            isLoading ? (
                                <CircularProgress size={24} color="info" />
                            ) : (
                                <Save />
                            )
                        }
                        type="button"
                        variant="contained"
                        color="primary"
                    >
                        Skapa
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
};

export default CreateTenant;
